import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";

/**
 * ReferralTracker — composant invisible monté globalement.
 *
 * Quand un visiteur arrive avec `?r=<USER_ID>` dans l'URL :
 *   1. Stocke le referrer_id dans localStorage (persisté jusqu'à l'inscription)
 *   2. Génère/récupère un visitor_session unique (UUID stocké localement)
 *   3. Notifie le backend pour incrémenter le compteur de visites du referrer
 *      (dédoublonné côté serveur par visitor_session)
 *
 * Plus tard, à l'inscription, AuthContext lit `referrer_id` depuis localStorage
 * et l'envoie au backend pour attribuer le signup.
 */

const REFERRER_KEY = "referrer_id";
const VISITOR_SESSION_KEY = "visitor_session";

function getOrCreateVisitorSession() {
  let s = localStorage.getItem(VISITOR_SESSION_KEY);
  if (!s) {
    s = `vs_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(VISITOR_SESSION_KEY, s);
  }
  return s;
}

export default function ReferralTracker() {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const referrerId = searchParams.get("r");
    if (!referrerId) return;

    // Persiste pour l'attribution future de l'inscription
    localStorage.setItem(REFERRER_KEY, referrerId);

    // Track la visite (dédupliquée serveur)
    const visitorSession = getOrCreateVisitorSession();
    api.post("/referrals/track", {
      referrer_id: referrerId,
      visitor_session: visitorSession,
    }).catch((e) => console.warn("Referral track failed:", e));

    // Nettoie l'URL pour ne pas polluer (mais garde le referrer en localStorage)
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("r");
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return null;
}

// Helper exporté pour récupérer le referrer stocké au moment de l'inscription
export function getStoredReferrerId() {
  return localStorage.getItem(REFERRER_KEY) || null;
}

// Helper exporté pour nettoyer le referrer après inscription (évite double attribution)
export function clearStoredReferrerId() {
  localStorage.removeItem(REFERRER_KEY);
}
